import { computed } from "vue";
import { useQueryManager } from "@/composables/useQueryManager";
import { useGlobalHandlers } from "@/composables/useGlobalHandlers";
import type {
  AiConversationFilters,
  AiConversationRow,
} from "./aiConversationsTypes";

/** Keeps filters and conversation selection in browser history, including direct links. */
export function useAiConversationQuery() {
  const { getQueryValue, updateQuery, parsers } = useQueryManager();
  const { globalErrorHandler } = useGlobalHandlers();
  const mode = getQueryValue(
    "mode",
    parsers.enum<"company" | "account">(["company", "account"])
  );
  const companyId = getQueryValue("company_id", parsers.positiveId);
  const accountId = getQueryValue("account_id", parsers.positiveId);
  const conversation = getQueryValue("conversation", parsers.positiveId);
  const types = getQueryValue("types", parsers.string);
  const since = getQueryValue("since", parsers.isoInstant);
  const until = getQueryValue("until", parsers.isoInstant);
  const staff = getQueryValue("staff", parsers.boolean);
  const test = getQueryValue("test", parsers.boolean);
  const legacyPeriod = getQueryValue("period", parsers.string);
  const openedAt = Date.now();

  const navigate = (updates: Record<string, string | undefined>) =>
    updateQuery(updates).catch(globalErrorHandler);
  const filters = computed<AiConversationFilters>({
    get: () => ({
      mode: mode.value ?? "company",
      companyId: mode.value !== "account" ? (companyId.value ?? null) : null,
      accountId: mode.value === "account" ? (accountId.value ?? null) : null,
      types: [
        ...new Set(
          (types.value ?? "")
            .split(",")
            .map((value) => value.trim())
            .filter(Boolean)
        ),
      ],
      since:
        since.value ??
        (!until.value && ["30", "90"].includes(legacyPeriod.value ?? "")
          ? new Date(
              openedAt - Number(legacyPeriod.value) * 86400000
            ).toISOString()
          : undefined),
      until: until.value,
      includeStaff: staff.value,
      includeTest: test.value,
    }),
    set: (value) => {
      void navigate({
        mode: value.mode,
        company_id:
          value.mode === "company" ? value.companyId?.toString() : undefined,
        account_id:
          value.mode === "account" ? value.accountId?.toString() : undefined,
        types: value.types.join(",") || undefined,
        since: value.since,
        until: value.until,
        staff: value.includeStaff ? "true" : undefined,
        test: value.includeTest ? "true" : undefined,
        period: undefined,
        conversation: undefined,
      });
    },
  });
  const selectedId = computed(() => conversation.value ?? null);

  function selectRow(row: AiConversationRow) {
    const scoped = filters.value.companyId || filters.value.accountId;
    void navigate({
      conversation: row.id.toString(),
      ...(!scoped
        ? filters.value.mode === "company"
          ? { company_id: row.company.id.toString(), account_id: undefined }
          : { account_id: row.account.id.toString(), company_id: undefined }
        : {}),
    });
  }
  function jump(id: number) {
    if (!parsers.positiveId(String(id))) return;
    void navigate({
      mode: "company",
      company_id: undefined,
      account_id: undefined,
      conversation: id.toString(),
    });
  }

  return { filters, selectedId, selectRow, jump };
}
